import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, Copy, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Logo, SectionLabel } from "@/components/bits";
import PostPurchase from "@/components/PostPurchase";

const TIER_LABELS = { day: "1 DAY", week: "1 WEEK", "15day": "15 DAYS", month: "1 MONTH", "3month": "3 MONTHS", lifetime: "LIFETIME", instant: "INSTANT" };

function CopyRow({ label, value, tid }) {
  if (!value) return null;
  const copy = () => { navigator.clipboard.writeText(value); toast.success(`${label} copied`); };
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-white/15 px-4 py-3.5" data-testid={`${tid}-row`}>
      <span className="font-mono text-[10px] tracking-[0.2em] text-white/40">{label}</span>
      <span className="flex items-center gap-2 min-w-0">
        <code className="font-mono text-sm text-white truncate" data-testid={`${tid}-value`}>{value}</code>
        <button onClick={copy} data-testid={`${tid}-copy`} className="text-white/50 hover:text-white shrink-0"><Copy size={15} /></button>
      </span>
    </div>
  );
}

export default function ManualPay() {
  const { sessionId } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | waiting | paid | rejected | missing
  const [channel, setChannel] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/pay/${sessionId}`);
      setInfo(data);
      if (data.payment_channel) setChannel((cur) => cur || data.payment_channel);
      if (data.payment_status === "paid") setState("paid");
      else if (data.status === "rejected") setState("rejected");
      else if (data.buyer_confirmed || data.status === "awaiting_review") setState("waiting");
      else setState("ready");
    } catch {
      setState("missing");
    }
  };

  useEffect(() => {
    load();
    return () => clearInterval(timer.current);
  }, [sessionId]);

  useEffect(() => {
    if (state === "waiting") {
      timer.current = setInterval(load, 5000);
      return () => clearInterval(timer.current);
    }
  }, [state]);

  const confirmSent = async () => {
    if (!channel) { toast.error("Choose Venmo or Apple Pay first"); return; }
    setBusy(true);
    try {
      await api.post(`/pay/${sessionId}/confirm`, { channel });
      setState("waiting");
      toast.success("Marked as sent — we're checking for it now");
    } catch {
      toast.error("Something went wrong — try again");
    }
    setBusy(false);
  };

  const copy = (k) => { navigator.clipboard.writeText(k); toast.success("Copied"); };

  if (state === "loading") {
    return <div className="min-h-screen flex items-center justify-center font-mono text-xs tracking-[0.3em] text-white/40">// LOADING...</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-28" data-testid="manual-pay-page">
      <div className="w-full max-w-lg">
        <div className="text-center"><Logo size={48} withText={false} /></div>

        {state === "missing" && (
          <div className="mt-8 text-center">
            <XCircle size={40} className="mx-auto text-white/60" />
            <h1 className="mt-5 font-display text-3xl uppercase">Order Not Found</h1>
            <Link to="/store" className="btn-inj inline-block mt-6 px-8 py-3.5 text-sm">BACK TO STORE</Link>
          </div>
        )}

        {(state === "ready" || state === "waiting") && info && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <SectionLabel className="justify-center mt-8">VENMO / APPLE PAY CHECKOUT</SectionLabel>
            <h1 className="mt-4 font-display text-4xl uppercase text-center">{info.product_name}</h1>
            <p className="mt-2 text-center font-mono text-xs tracking-[0.15em] text-white/50">
              {(TIER_LABELS[info.tier] || info.tier)}{info.quantity > 1 ? ` × ${info.quantity}` : ""}
            </p>

            <div className="mt-8 rounded-2xl border border-white/20 p-6 space-y-4">
              <div className="text-center">
                <div className="font-mono text-[10px] tracking-[0.2em] text-white/40">SEND EXACTLY</div>
                <div className="font-display text-5xl mt-1" data-testid="pay-amount">${(info.amount || 0).toFixed(2)}</div>
              </div>

              <CopyRow label="PAYMENT NOTE / REF (REQUIRED)" value={info.ref_code} tid="ref-code" />
              <CopyRow label="VENMO (FRIENDS & FAMILY)" value={info.venmo_handle} tid="venmo" />
              {info.venmo_handle && (
                <p className="font-mono text-[10px] tracking-[0.12em] text-white/70 rounded-xl border border-white/20 bg-white/[0.05] px-4 py-2.5" data-testid="venmo-spelling-note">
                  // CHECK THE SPELLING: <span className="text-white">{info.venmo_handle}</span> — "INJECTD" WITH NO "E" AT THE END. SENDING TO THE WRONG NAME DELAYS YOUR KEY.
                </p>
              )}
              <CopyRow label="APPLE PAY" value={info.applepay_handle} tid="applepay" />

              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="ff-note">
                <p className="font-mono text-[10px] tracking-[0.12em] text-white/60 leading-relaxed">
                  // VENMO: SEND AS <span className="text-white">FRIENDS &amp; FAMILY</span> (NO PURCHASE PROTECTION TOGGLE) SO IT LANDS INSTANTLY.
                  WHICHEVER RAIL YOU USE, PUT YOUR REF CODE <span className="text-white">{info.ref_code}</span> IN THE PAYMENT NOTE — THAT'S HOW WE MATCH IT TO YOUR ORDER.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-px rounded-xl overflow-hidden bg-white/10 border border-white/10" data-testid="payment-channel-picker">
                {[["venmo", "VENMO"], ["applepay", "APPLE PAY"]].map(([value, label]) => (
                  <button key={value} type="button" onClick={() => setChannel(value)} data-testid={`pay-channel-${value}`}
                    className={`py-3 font-mono text-[10px] tracking-[0.16em] transition-colors ${channel === value ? "bg-white text-black" : "bg-black text-white/50 hover:text-white"}`}>
                    {label}
                  </button>
                ))}
              </div>

              {state === "ready" ? (
                <button onClick={confirmSent} disabled={busy} data-testid="confirm-sent-btn"
                  className="btn-inj w-full py-4 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} I'VE SENT THE PAYMENT
                </button>
              ) : (
                <div className="text-center rounded-2xl border border-white/15 p-5" data-testid="waiting-panel">
                  <Clock size={22} className="mx-auto text-white/70 animate-pulse" />
                  <p className="mt-3 font-mono text-xs tracking-[0.15em] text-white/70">// PAYMENT SENT{channel ? ` VIA ${channel === "applepay" ? "APPLE PAY" : "VENMO"}` : ""} — WAITING FOR CONFIRMATION</p>
                  <p className="mt-2 text-xs text-white/40">Usually confirmed within minutes. Your keys appear here + hit your email the moment it's approved.</p>
                </div>
              )}
            </div>

            <p className="mt-5 text-center font-mono text-[10px] tracking-[0.15em] text-white/30">
              WRONG AMOUNT OR MISSING REF CODE = DELAYED DELIVERY · <Link to="/store" className="underline hover:text-white">BACK TO STORE</Link>
            </p>
          </motion.div>
        )}

        {state === "paid" && info && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-8 text-center">
            <CheckCircle2 size={44} className="mx-auto text-white" />
            <h1 className="mt-5 font-display text-4xl uppercase">Payment Confirmed</h1>
            {info.order && (info.order.keys?.length || info.order.license_key) ? (
              <div className="mt-8 rounded-2xl border border-white/25 p-6 text-left" data-testid="license-panel">
                <div className="font-mono text-[10px] tracking-[0.2em] text-white/40">{info.order.product_name}</div>
                {(info.order.keys?.length ? info.order.keys : [info.order.license_key]).filter(Boolean).map((k, i) => (
                  <div key={i} className="mt-3 flex items-center gap-3">
                    <code data-testid={`license-key-${i}`} className="font-mono text-white bg-white/[0.08] px-3 py-2 rounded-lg border border-white/15 flex-1 break-all">{k}</code>
                    <button onClick={() => copy(k)} data-testid={`copy-license-key-${i}`} className="text-white/60 hover:text-white"><Copy size={18} /></button>
                  </div>
                ))}
                {info.order.key_status !== "delivered" && (
                  <p className="mt-4 font-mono text-[10px] tracking-[0.15em] text-white/60">// REMAINING KEYS BEING PREPARED — EMAILED SHORTLY</p>
                )}
              </div>
            ) : (
              <p className="mt-6 font-mono text-xs tracking-[0.15em] text-white/50" data-testid="license-pending-panel">
                // PAYMENT CONFIRMED — YOUR KEYS ARE BEING PREPARED AND WILL BE EMAILED SHORTLY
              </p>
            )}
            {info.order && <PostPurchase sessionId={sessionId} order={info.order} productSlug={info.order?.slug} />}
            <Link to="/store" className="btn-ghost inline-block mt-8 px-8 py-4 text-sm">KEEP SHOPPING</Link>
          </motion.div>
        )}

        {state === "rejected" && (
          <div className="mt-8 text-center">
            <XCircle size={40} className="mx-auto text-white/60" />
            <h1 className="mt-5 font-display text-3xl uppercase">Payment Not Found</h1>
            <p className="mt-3 text-white/55 text-sm">We couldn't match a payment to this order. If you did send it, ping us on Discord with your ref code <span className="font-mono text-white">{info?.ref_code}</span>.</p>
            <Link to="/store" className="btn-inj inline-block mt-6 px-8 py-3.5 text-sm">BACK TO STORE</Link>
          </div>
        )}
      </div>
    </div>
  );
}
